import React from "react";
import { Link } from "react-router-dom";
import { Gem } from "lucide-react";
import { Button } from "@/components/ui/button";

// Shared marketing chrome (nav + footer) used by the P1 SEO public
// pages: /pricing, /about, /contact, /features,
// /features/autopilot, /features/brand-learning. Kept as a single
// component so nav/footer link edits (adding routes, renaming) only
// have to happen in one place. Existing marketing pages
// (AiSocialMediaManagement, SocialMediaScheduler, WhatsNewPublic)
// still ship their own inline chrome — leaving those untouched
// preserves the copy-locked tests in marketing_batch1.test.js.

export function MarketingHeader({ activePath = "" }) {
  const linkCls = (path) =>
    "hover:text-[color:var(--text-1)] " +
    (activePath === path ? "text-[color:var(--text-1)]" : "");
  return (
    <header
      className="sticky top-0 z-30 backdrop-blur bg-[color:rgba(11,13,16,0.72)] border-b border-[color:var(--border-subtle)]"
      data-testid="marketing-header"
    >
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 h-14 flex items-center justify-between gap-3">
        <Link
          to="/"
          className="flex items-center gap-2 min-w-0"
          data-testid="marketing-logo"
          aria-label="Postmoro home"
        >
          <div className="h-7 w-7 rounded-md bg-gradient-to-br from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] text-[color:var(--bg-0)] flex items-center justify-center shadow-[0_4px_18px_rgba(27,168,159,0.45)] shrink-0">
            <Gem className="h-4 w-4" />
          </div>
          <span className="font-display text-lg font-semibold tracking-tight truncate">
            Postmoro
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-6 text-sm text-[color:var(--text-2)]">
          <Link to="/features" className={linkCls("/features")} data-testid="marketing-nav-features">Features</Link>
          <Link to="/pricing" className={linkCls("/pricing")} data-testid="marketing-nav-pricing">Pricing</Link>
          <Link to="/affiliates" className={linkCls("/affiliates")} data-testid="marketing-nav-affiliates">Affiliates</Link>
          <Link to="/about" className={linkCls("/about")} data-testid="marketing-nav-about">About</Link>
          <Link to="/contact" className={linkCls("/contact")} data-testid="marketing-nav-contact">Contact</Link>
        </nav>
        <div className="flex items-center gap-2 shrink-0">
          <Link to="/login">
            <Button
              variant="ghost"
              className="text-[color:var(--text-2)] hover:text-[color:var(--text-1)] hidden sm:inline-flex"
              data-testid="marketing-signin"
            >
              Sign in
            </Button>
          </Link>
          <Link to="/login">
            <Button
              className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
              data-testid="marketing-start-free-header"
            >
              Start free
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

export function MarketingFooter() {
  return (
    <footer
      className="mt-24 border-t border-[color:var(--border-subtle)] py-10 text-sm text-[color:var(--text-3)]"
      data-testid="marketing-footer"
    >
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <div className="h-5 w-5 rounded bg-gradient-to-br from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] text-[color:var(--bg-0)] flex items-center justify-center">
            <Gem className="h-3 w-3" />
          </div>
          <span>© {new Date().getFullYear()} Postmoro LLC</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <Link to="/features" className="hover:text-[color:var(--text-1)]" data-testid="marketing-footer-features">Features</Link>
          <Link to="/pricing" className="hover:text-[color:var(--text-1)]" data-testid="marketing-footer-pricing">Pricing</Link>
          <Link to="/about" className="hover:text-[color:var(--text-1)]" data-testid="marketing-footer-about">About</Link>
          <Link to="/contact" className="hover:text-[color:var(--text-1)]" data-testid="marketing-footer-contact">Contact</Link>
          <Link to="/whats-new" className="hover:text-[color:var(--text-1)]" data-testid="marketing-footer-whats-new">What&apos;s New</Link>
          <Link to="/affiliates" className="hover:text-[color:var(--text-1)]" data-testid="marketing-footer-affiliates">Affiliates</Link>
          <Link to="/privacy" className="hover:text-[color:var(--text-1)]" data-testid="marketing-footer-privacy">Privacy</Link>
          <Link to="/terms" className="hover:text-[color:var(--text-1)]" data-testid="marketing-footer-terms">Terms</Link>
        </div>
      </div>
    </footer>
  );
}
